import React from 'react';
import HeroSection from '../HeroSection';
import ServicesList from '../ServicesList';
import WhyChooseUs from '../WhyChooseUs';
import FeaturedApp from '../FeaturedApp';
import AIAgents from '../AIAgents';
import BlogPreview from '../BlogPreview';
import Contact from '../Contact';
import SEO from '../SEO';

export default function Home() {
  // Comprehensive schema data for maximum SEO impact
  const homepageSchemas = [
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "BetterByAI",
      "url": "https://betterbyai.com",
      "description": "Transform Your Business with Intelligent AI Solutions",
      "potentialAction": {
        "@type": "SearchAction",
        "target": "https://betterbyai.com/search?q={search_term_string}",
        "query-input": "required name=search_term_string"
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      "url": "https://betterbyai.com",
      "name": "BetterByAI",
      "logo": "https://betterbyai.com/logo.png",
      "description": "UK-based AI solutions agency specialising in business automation and revenue generation through intelligent systems",
      "sameAs": [
        "https://twitter.com/betterbyai",
        "https://www.linkedin.com/company/betterbyai",
        "https://www.facebook.com/betterbyai"
      ]
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "How can AI improve my business operations?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "AI can automate repetitive tasks, analyse large datasets for insights, enhance customer experiences through personalisation, optimise resource allocation, and provide data-driven decision support, resulting in improved efficiency and revenue growth."
          }
        },
        {
          "@type": "Question",
          "name": "What AI solutions does BetterByAI offer?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "We offer custom AI agents, RAG (Retrieval-Augmented Generation) systems, AI chatbots, process automation, predictive analytics solutions, and strategic AI implementation consulting tailored to your specific business needs."
          }
        },
        {
          "@type": "Question",
          "name": "How long does it take to implement AI solutions?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Implementation timelines vary based on complexity. Simple AI implementations can be completed within weeks, while more complex enterprise solutions typically take 2-3 months. Our team works efficiently to deliver ROI as quickly as possible."
          }
        },
        {
          "@type": "Question",
          "name": "What ROI can I expect from AI implementation?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Our clients typically see 20-40% efficiency improvements in automated processes, significant cost reductions, and new revenue opportunities. The exact ROI depends on your specific use case, which we analyse during our consultation process."
          }
        },
        {
          "@type": "Question",
          "name": "Do I need technical expertise to use your AI solutions?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "No. We design our AI solutions to be user-friendly and integrate seamlessly with your existing workflows. Our team provides comprehensive training and ongoing support to ensure smooth adoption across your organisation."
          }
        },
        {
          "@type": "Question",
          "name": "Is AI implementation suitable for small businesses or just enterprises?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "AI implementation is beneficial for businesses of all sizes. For small businesses, we offer scalable solutions that deliver immediate ROI without requiring large upfront investments. Our approach focuses on identifying high-impact areas where AI can provide the most significant benefits within your budget constraints."
          }
        },
        {
          "@type": "Question",
          "name": "How does BetterByAI ensure data security during AI implementation?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Data security is our priority. We implement industry-leading encryption protocols, access controls, and secure infrastructure. All AI solutions are built with privacy-by-design principles, ensuring GDPR compliance and data protection. We also provide comprehensive documentation and training on security best practices for your team."
          }
        },
        {
          "@type": "Question",
          "name": "How do you integrate AI solutions with existing business systems?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Our integration process is designed for minimal disruption. We begin with a thorough assessment of your current systems, develop custom API connections and middleware when needed, and use industry-standard integration protocols. We offer phased implementation with thorough testing to ensure seamless operation with your CRM, ERP, or custom systems."
          }
        },
        {
          "@type": "Question",
          "name": "What industries benefit most from your AI solutions?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "While our AI solutions deliver value across sectors, we've seen exceptional results in healthcare (patient care optimisation, diagnostic assistance), financial services (risk assessment, fraud detection), manufacturing (predictive maintenance, quality control), retail (inventory management, customer insights), and professional services (document processing, client engagement)."
          }
        }
      ]
    },
    // New WebPage schema with detailed content sections
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "BetterByAI - Transformative AI Solutions for UK Businesses",
      "description": "Specialised AI consultancy offering bespoke automation, RAG systems, and AI agents to boost efficiency and reduce operational costs for UK businesses.",
      "url": "https://betterbyai.com",
      "isPartOf": {
        "@type": "WebSite",
        "@id": "https://betterbyai.com/#website"
      },
      "primaryImageOfPage": {
        "@type": "ImageObject",
        "url": "https://betterbyai.com/logo.png"
      },
      "mainEntity": {
        "@type": "ItemList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "AI Business Solutions",
            "description": "Bespoke AI services including custom agents, RAG systems and automation workflows designed for UK businesses seeking competitive advantage through technology."
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "VoiceCalendar SAAS Platform",
            "description": "Our breakthrough calendar management system utilising advanced voice commands and intelligent function calling to orchestrate appointments, gather insights, and perform administrative tasks without manual intervention."
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": "AI Agents & Automation",
            "description": "Custom-built AI agents that handle complex workflows, improve customer engagement and reduce operational overhead by up to 40%."
          },
          {
            "@type": "ListItem",
            "position": 4,
            "name": "Process Optimisation",
            "description": "Data-driven process improvement and workflow automation tailored to your specific business challenges and operational requirements."
          },
          {
            "@type": "ListItem",
            "position": 5,
            "name": "RAG Systems & Knowledge Management",
            "description": "Retrieval-Augmented Generation solutions that transform your business documents into intelligent knowledge bases with contextual understanding."
          }
        ]
      },
      "speakable": {
        "@type": "SpeakableSpecification",
        "cssSelector": [".hero-heading", ".services-intro", ".why-choose-section"]
      }
    },
    
    // Service offering schema
    {
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": "AI Business Solutions",
      "provider": {
        "@type": "Organization",
        "name": "BetterByAI",
        "@id": "https://betterbyai.com/#organization"
      },
      "description": "Custom AI development and implementation services for UK businesses seeking improved efficiency and competitive advantage",
      "offers": {
        "@type": "Offer",
        "description": "Tailored AI solutions with proven ROI and efficiency improvements of 20-40%"
      },
      "areaServed": {
        "@type": "Country",
        "name": "United Kingdom"
      },
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "AI Services",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Custom AI Agents",
              "description": "Intelligent autonomous systems designed to handle specific business operations and customer interactions."
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "RAG Systems",
              "description": "Knowledge management solutions that combine retrieval-based and generative AI capabilities."
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "AI Web Applications",
              "description": "Bespoke web applications with embedded AI capabilities for enhanced user experiences."
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Process Automation",
              "description": "AI-powered workflows that streamline operations and reduce manual effort."
            }
          }
        ]
      }
    },
    // Add new SoftwareApplication schema for more detailed product representation
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "VoiceCalendar",
      "applicationCategory": "BusinessApplication",
      "operatingSystem": "Web-based",
      "offers": {
        "@type": "Offer",
        "price": "19.99",
        "priceCurrency": "GBP"
      },
      "description": "Revolutionary SAAS calendar management platform using voice commands and intelligent function calling to automate scheduling, provide insights, and perform administrative tasks.",
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.8",
        "ratingCount": "127"
      },
      "featureList": "Voice command scheduling, Smart meeting reminders, Predictive scheduling, Participant analytics, Document preparation, Cross-platform integration, Activity summarisation",
      "screenshot": "https://betterbyai.com/voicecalendar-dashboard.jpg"
    }
  ];

  return (
    <>
      <SEO 
        title="AI Business Solutions | Custom AI Development & Implementation | BetterByAI"
        description="UK-based AI solutions agency delivering custom AI agents, RAG systems and voice applications. Boost efficiency by 40% with our bespoke AI implementations."
        canonical="https://betterbyai.com"
        schemaMarkup={homepageSchemas}
        speakable={true}
        keywords="AI solutions, RAG bots, machine learning services, AI web applications, business automation, ai agency, agency uk"
      />
      
      {/* Hidden content for search engines - using visually-hidden/sr-only pattern */}
      <div className="sr-only" aria-hidden="true">
        <h1>BetterByAI - Specialist AI Solutions for UK Businesses</h1>
        
        <section>
          <h2>Transform Your Business with Our AI Solutions</h2>
          <p>Welcome to BetterByAI, the leading AI consultancy in Bedfordshire and London helping businesses throughout the UK leverage artificial intelligence for competitive advantage. Our bespoke AI solutions have demonstrably improved efficiency by up to 40% whilst reducing operational costs across diverse industries.</p>
          <p>Whether you're seeking to automate routine workflows, extract insights from untapped data, or enhance customer experiences through intelligent systems, our seasoned team of AI specialists delivers results-driven solutions tailored to your precise requirements.</p>
        </section>
        
        <section>
          <h2>Comprehensive AI Services for UK Businesses</h2>
          <p>Our portfolio encompasses the full spectrum of AI implementation services, including:</p>
          <ul>
            <li><strong>Custom AI Agents</strong> - Intelligent systems that operate autonomously to handle specific business tasks, customer enquiries, and complex workflows</li>
            <li><strong>RAG Systems</strong> - Retrieval-Augmented Generation solutions that transform your business documents into intelligent knowledge bases with contextual understanding</li>
            <li><strong>AI Web Applications</strong> - Bespoke web platforms with embedded AI capabilities, including our revolutionary VoiceCalendar system that uses natural speech and function calling to manage appointments, provide business intelligence, and automate administrative tasks</li>
            <li><strong>Process Automation</strong> - End-to-end workflow optimisation using AI to identify inefficiencies and implement automated solutions</li>
            <li><strong>Data Science & Analytics</strong> - Advanced data analysis and predictive modelling to uncover business insights and enable data-driven decisions</li>
          </ul>
        </section>
        
        <section>
          <h2>Why Choose BetterByAI for Your AI Implementation</h2>
          <p>Unlike large consultancies with generic approaches, we deliver personalised AI solutions that address your specific business challenges. Our proven process includes:</p>
          <ul>
            <li>Thorough assessment of your current operations and strategic objectives</li>
            <li>Development of tailored AI implementation roadmaps with clear ROI projections</li>
            <li>Seamless integration with existing systems and workflows</li>
            <li>Comprehensive staff training and adoption support</li>
            <li>Ongoing optimisation and performance monitoring</li>
          </ul>
          <p>Our clients typically achieve 20-40% improvements in operational efficiency, significant cost reductions, and enhanced customer satisfaction scores following our AI implementations.</p>
        </section>
        
        <section>
          <h2>Featured Application: VoiceCalendar</h2>
          <p>We've fundamentally transformed how businesses manage their calendars with our voice-powered SAAS platform. This groundbreaking system allows users to simply speak commands—"reschedule tomorrow's meeting with Sarah to Friday" or "block out Thursday afternoon for project work"—and the intelligent backend handles everything automatically. The platform doesn't just passively take commands; it proactively suggests optimisations based on your working patterns.</p>
          <p>Many of our clients have reported a remarkable 35% reduction in appointment no-shows, as VoiceCalendar automatically sends intelligently timed reminders tailored to each recipient's communication preferences. By analysing historical attendance patterns, meeting outcomes, and participant feedback, the system continuously improves scheduling quality whilst reducing administrative workload.</p>
          <p>The function calling architecture enables seamless integration with other business tools—your voice command to "prepare for tomorrow's client meeting" will gather relevant documents, summarise previous interactions, and even brief you on recent developments from their LinkedIn activity. This truly represents the next generation of calendar management, moving beyond basic scheduling to become your intelligent administrative partner.</p>
        </section>
        
        <section>
          <h2>Advanced AI Agents for Business Transformation</h2>
          <p>Our custom AI agents handle complex tasks autonomously, from customer service interactions to data processing workflows. These intelligent systems adapt to your business processes, continuously improving their performance through machine learning while maintaining brand-consistent communications.</p>
        </section>
        
        <section>
          <h2>Industry Insights from Our Knowledge Centre</h2>
          <p>Stay informed with our regularly updated blog featuring practical AI implementation strategies, case studies, and industry trends. Our content helps business leaders make informed decisions about AI adoption with actionable, jargon-free guidance.</p>
        </section>
        
        <section>
          <h2>Contact BetterByAI for Strategic AI Implementation</h2>
          <p>Ready to explore how AI can transform your business operations? Our consultants are available to discuss your specific challenges and objectives. Schedule a free initial consultation to assess potential AI applications for your organisation.</p>
        </section>
      </div>
      
      <HeroSection />
      <ServicesList />
      <WhyChooseUs />
      <FeaturedApp />
      <AIAgents />
      <BlogPreview />
      <Contact />
    </>
  );
}